﻿import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Select from "react-select";
import { useToasts } from "react-toast-notifications";

import Fetching from "@/components/Fetching/Fetching";
import BusinessCenterSectorTable from "@/components/BusinessCenter/BusinessCenterSectorTable";
import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";

import { validRoles } from "@/services/Utils";
import api from "@/services/api.service";

import { base_business_center_parents, customStyles } from "../business_center.constants";
import classes from "../../../maintainers.module.scss";

const BusinessCenterParentForm = ({ confirmSaveParent, estates_parents, estates, categories, species, varieties, quarters, crops, handleDelete }) => {
	const { option, slug } = useParams();
	const { addToast } = useToasts();

	const { customer } = useSelector((state) => state.auth.user);

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isManager = validRoles(userRoles, ["administrador", "gerente_agricola", "administrador_campo"]);

	const location = useLocation();
	const navigate = useNavigate();

	const [data, setData] = useState({ ...base_business_center_parents });
	const [isFetching, setIsFetching] = useState(true);

	const handleClose = () => {
		emptyFields();
		if (location.pathname.includes("configuracion")) {
			navigate("/configuracion/centro_costo");
			return;
		}

		if (location.pathname.includes("mantenedores")) {
			navigate("/mantenedores/centro_costo");
			return;
		}

		if (location.pathname.includes("mis_campos")) {
			navigate("/mis_campos");
			return;
		}
	};

	const emptyFields = () => {
		setData({ ...base_business_center_parents });
	};

	const handleAddRow = () => {
		if (data.estate_id === "") {
			addToast("Debes seleccionar una bodega para poder agregar sectores", { appearance: "info" });
			return;
		}

		const newBusinessCenter = [
			...data.businessCenters,
			{
				hash: uuidv4(),
				name: "",
				estateParents: [],
				estates: [],
				area: "",
				specie: "",
				variety: "",
				isNew: true,
				sharedIds: "",
				slug: "",
				customer: "",
				category_id: "",
				plantation_distance: "",
				plantation_distance_end: "",
				rootstock: "",
				type_irrigation: "",
				conduction_system: "",
				year: "",
				business_center_type_id: "",
				estate_id: data.estate_id,
				species: [],
			},
		];

		setData({
			...data,
			businessCenters: newBusinessCenter,
		});
	};

	const handleInputChange = (index, key, value) => {
		const businessCenters = [...data.businessCenters];
		businessCenters[index][key] = value;
		setData({
			...data,
			businessCenters,
		});
	};

	const validSave = () => {
		if (!data.businessCenters || data.businessCenters.length === 0) {
			addToast("Debes agregar al menos un sector para guardar un centro de costo", { appearance: "error" });
			return false;
		}
		if (!data.estates || data.estates.length === 0) {
			addToast("Debes seleccionar al menos una bodega para guardar un centro de costo", { appearance: "error" });
			return false;
		}

		const isValid =
			!data.name ||
			data.name.trim() === "" ||
			data.businessCenters.some(
				(center) =>
					!center.name ||
					center.name.trim() === "" ||
					!center.specie ||
					String(center.specie).trim() === "" ||
					!center.variety ||
					center.variety.trim() === "",
			);

		if (isValid) {
			addToast(
				"Los centros de costos deben tener un nombre general y sectores que contengan su respectivo nombre, campo/bodega, superficie, especie y variedad",
				{ appearance: "error" },
			);
		}

		return !isValid;
	};

	const handleSave = () => {
		if (validSave()) {
			confirmSaveParent({ ...data }, setIsFetching, afterSave);
		}
	};

	const afterSave = () => {
		handleClose();
		addToast("Centro de costo guardado correctamente.", {
			appearance: "success",
		});
	};

	const getSpecies = (selected_estates) => {
		let new_species = [];

		species.forEach((s) => {
			s.estates.forEach((es) => {
				if (selected_estates.includes(es)) {
					if (!new_species.find((ns) => Number(ns.value) === Number(s.value))) {
						if (Number(s.category_id) === Number(data.category_id)) {
							new_species.push(s);
							return;
						}
					}
				}
			});
		});

		return new_species;
	};

	const removeRow = (index, _isNew) => {
		const item = data.businessCenters[index];
		handleDelete(item, "business_center");
	};

	useEffect(() => {
		setIsFetching(true);
		if (option && slug && ["crear_grupo", "editar_grupo"].includes(option)) {
			api.get("/admin/business_center/detail", { slug, option }).then((response) => {
				const { detail } = response;
				if (detail) {
					let dataDetail = {
						...detail,
						plantation_distance: "",
						plantation_distance_end: "",
						rootstock: "",
						type_irrigation: "",
						conduction_system: "",
						year: "",
						business_center_type_id: "",
					};

					setData(dataDetail);
				} else {
					addToast("Ocurrió un problema al cargar el centro de costo seleccionado.", {
						appearance: "error",
					});
					handleClose();
				}
				setIsFetching(false);
			});
		} else {
			let newData = { ...base_business_center_parents };

			if (estates_parents.length === 1) {
				newData.estate_parent_id = estates_parents[0].value;

				newData.businessCenters = newData.businessCenters.map((bc) => {
					bc.estate_parent_id = estates_parents[0].value;
					return bc;
				});
			}
			if (estates && estates.length === 1) {
				newData.estates = [estates[0].value];

				newData.businessCenters = newData.businessCenters.map((bc) => {
					bc.estates = [estates[0].value];
					return bc;
				});
			}

			setData(newData);

			setIsFetching(false);
		}

		if (!Object.prototype.hasOwnProperty.call(data, "estateParents") && !Object.prototype.hasOwnProperty.call(data, "estates")) {
			setData({
				...data,
				estateParents: estates_parents,
				estates: estates,
			});
		}

		// eslint-disable-next-line
	}, [slug, option]);

	return (
		<Modal
			show={option && ["crear_grupo", "editar_grupo"].includes(option) ? true : false}
			onHide={handleClose}
			backdrop="static"
			keyboard={false}
			className={classes.main_form}
			size="xl"
		>
			<Modal.Header closeButton>
				<h4 className={classes.modal_title}>Centro de Costo</h4>
			</Modal.Header>
			<Modal.Body>
				{isFetching ? (
					<Fetching topMargin={"1em"} />
				) : (
					<>
						<Row>
							<Col md={4} xs={12}>
								<Form.Group>
									<Form.Label className={classes.label}>Nombre Centro de Costo</Form.Label>
									<Form.Control
										value={data.name}
										onChange={(e) =>
											setData({
												...data,
												name: e.currentTarget.value,
											})
										}
									/>
								</Form.Group>
							</Col>

							{!!customer.use_estates_parents && (
								<Col md={4} xs={12}>
									<Form.Group>
										<Form.Label className={classes.label}>
											{customer.customer_screens.find((cs) => cs.screen_id === 38)
												? customer.customer_screens.find((cs) => cs.screen_id === 38)?.name
												: "Grupos Campos"}
										</Form.Label>
										<Select
											value={estates_parents && estates_parents.filter((c) => Number(c.value) === Number(data.estate_parent_id))}
											placeholder={<div>Seleccione</div>}
											options={estates_parents ? estates_parents : []}
											onChange={(value) => {
												const availableEstates = value
													? estates.filter((e) => Number(e.estate_parent_id) === Number(value.value)).map((e) => e.value)
													: [];

												let newData = { ...data };
												newData.estate_parent_id = value ? value.value : "";
												newData.estates = availableEstates;
												newData.businessCenters.map((bc) => {
													bc.estate_parent_id = value ? value.value : "";
													bc.estates = availableEstates;
													return bc;
												});

												setData(newData);
											}}
											components={{
												NoOptionsMessage,
											}}
											isClearable
										/>
									</Form.Group>
								</Col>
							)}

							<Col md={4} xs={12}>
								<Form.Group>
									<Form.Label className={classes.label}>
										{customer.customer_screens.find((cs) => cs.screen_id === 51)
											? customer.customer_screens.find((cs) => cs.screen_id === 51)?.name
											: "Campos"}
									</Form.Label>
									<Select
										value={estates && estates.filter((estate) => data.estates.includes(estate.value))}
										placeholder={<div>Seleccione</div>}
										options={
											data.estate_parent_id
												? estates.filter((estate) => Number(estate.estate_parent_id) === Number(data.estate_parent_id))
												: estates
													? estates
													: []
										}
										onChange={(value) => {
											const selectEstates = value ? value.map((v) => v.value) : [];

											let newData = { ...data };
											newData.estates = selectEstates;
											newData.businessCenters.map((bc) => {
												bc.estates = selectEstates;
												return bc;
											});

											setData(newData);
										}}
										components={{
											NoOptionsMessage,
										}}
										isDisabled={!isManager}
										styles={customStyles}
										isMulti={true}
									/>
								</Form.Group>
							</Col>
						</Row>

						<Row>
							<Col md={3} xs={12}>
								<Form.Group className={classes.group_form}>
									<Form.Label className={classes.label}>Categoría</Form.Label>
									<Select
										value={categories && categories.filter((c) => Number(c.value) === Number(data.category_id))}
										placeholder={<div>Seleccione Paso 1</div>}
										options={
											categories ? categories.filter((c) => c.estate_ids && c.estate_ids.some((eid) => data.estates.includes(eid))) : []
										}
										onChange={(value) =>
											setData({
												...data,
												category_id: value ? value.value : "",
											})
										}
										components={{
											NoOptionsMessage,
										}}
										isDisabled={!isManager}
										styles={customStyles}
									/>
								</Form.Group>
							</Col>
							<Col md={3} xs={12}>
								<Form.Group className={classes.group_form}>
									<Form.Label className={classes.label}>Paso 1</Form.Label>
									<Select
										isMulti
										value={species && species.filter((s) => data.species && data.species.includes(s.value))}
										placeholder={<div>Seleccione Paso 1</div>}
										options={getSpecies(data.estates)}
										onChange={(value) => {
											let newSpecies = [];
											value &&
												value.map((specie) => {
													newSpecies.push(specie.value);
													return null;
												});
											setData({
												...data,
												species: newSpecies,
											});
										}}
										components={{
											NoOptionsMessage,
										}}
										isDisabled={!isManager}
										styles={customStyles}
									/>
								</Form.Group>
							</Col>

							<Col md={3} xs={12}>
								<Form.Group className={classes.group_form}>
									<Form.Label className={classes.label}>Paso 2</Form.Label>
									<Select
										isMulti
										value={varieties && varieties.filter((v) => data.varieties && data.varieties.includes(v.value))}
										placeholder={<div>Seleccione Paso 2</div>}
										options={data.species.length !== 0 ? varieties.filter((v) => data.species.includes(v.specie_id)) : []}
										onChange={(value) => {
											let newVarieties = [];
											value &&
												value.map((variety) => {
													newVarieties.push(variety.value);
													return null;
												});
											setData({
												...data,
												varieties: newVarieties,
											});
										}}
										components={{
											NoOptionsMessage,
										}}
										isDisabled={!isManager}
										styles={customStyles}
									/>
								</Form.Group>
							</Col>

							<Col md={3} xs={12}>
								<Form.Group className={classes.group_form}>
									<Form.Label className={classes.label}>Paso 3</Form.Label>
									<Select
										isMulti
										value={quarters && quarters.filter((q) => data.quarters && data.quarters.includes(q.value))}
										placeholder={<div>Seleccione Paso 3</div>}
										options={data.varieties.length !== 0 ? quarters.filter((q) => data.varieties.includes(q.variety_id)) : []}
										onChange={(value) => {
											let newQuarters = [];
											value &&
												value.map((quarter) => {
													newQuarters.push(quarter.value);
													return null;
												});
											setData({
												...data,
												quarters: newQuarters,
											});
										}}
										components={{
											NoOptionsMessage,
										}}
										isDisabled={!isManager}
										styles={customStyles}
									/>
								</Form.Group>
							</Col>
						</Row>

						<BusinessCenterSectorTable
							showEstateParentColumn={false}
							data={data}
							handleInputChange={handleInputChange}
							addRow={handleAddRow}
							removeRow={removeRow}
							showBusinessCenter={false}
							crops={crops}
							isImplementation={false}
						/>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="success" onClick={() => handleSave()}>
					Guardar
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default BusinessCenterParentForm;
